import React, { useEffect, useMemo, useState } from "react";
import "./ContactDetails.css";
import myImage from "../../../assets/images/contact.svg.png";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { routeNames } from "../../../consts/routesNames";
import trash from "../../../assets/images/trash.svg";
import Button from "../../../components/common/button/Button";
import appConfig from "../../../config.json";
import { axios } from "../../../utils/axiosConfig";
import { BACKEND_URL } from "../../../constants";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useAuth, useCan } from "../../../auth/hooks/usePermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { isAdminRole } from "../../../auth/utils/roles";
import { getContactDetails } from "../../../apis/contactsApis/getContactDetails";
import { openModal } from "../../../redux/modal/modalActions";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import EntityLinksSection from "../../../components/entityLinks/EntityLinksSection";
import { formatLinkedUserDisplay, formatLinkedUserRole } from "../../../auth/utils/contactLink";

function unwrapDetailPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && raw.data !== undefined && raw.data !== null) {
    return raw.data;
  }
  return raw;
}

function ContactDetails() {
  const backEndUrl = `${BACKEND_URL}`;
  const canUpdateContactImagePermission = useCan(PERMISSIONS.CONTACTS.IMAGE_UPDATE);
  const { canUpdateOwnedRecord } = useUpdateOwnership();
  const { user } = useAuth();
  const isAdminUser = isAdminRole(user?.role);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const stateRow = location.state?.data;

  const resolvedContactId = useMemo(() => {
    const raw = searchParams.get("id");
    const parsed = raw != null && raw !== "" ? parseInt(raw, 10) : NaN;
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return stateRow?.id;
  }, [searchParams, stateRow]);

  const [contactData, setContactData] = useState(() => stateRow ?? null);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [relatedContacts, setRelatedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemImage, setItemImage] = useState(stateRow?.profile_picture ?? null);

  const canUpdateContactImage = canUpdateOwnedRecord(
    contactData?.created_by,
    canUpdateContactImagePermission,
  );

  useEffect(() => {
    if (!resolvedContactId) {
      dispatch(openModal(<ResponseModal text="No contact selected. Open a contact from the list." isError={true} />));
      navigate(routeNames.CONTACTS, { replace: true });
      return;
    }

    let cancelled = false;

    const loadContactDetail = async () => {
      setLoading(true);
      const detailsRes = await getContactDetails(resolvedContactId);
      if (cancelled) return;

      if (detailsRes?.error_code === 2 || detailsRes?.error_code === 3) {
        dispatch(openModal(<ResponseModal text={detailsRes.error_message ?? "Could not load contact details."} isError={true} />));
        navigate(routeNames.CONTACTS, { replace: true });
        return;
      }

      const detail = unwrapDetailPayload(detailsRes.res?.data);

      if (cancelled || detail == null || detail.id == null) {
        if (!cancelled) {
          dispatch(openModal(<ResponseModal text="Could not load contact details." isError={true} />));
          navigate(routeNames.CONTACTS, { replace: true });
        }
        return;
      }

      if (Number(detail.id) !== Number(resolvedContactId)) return;

      setContactData(detail);
      setItemImage(detail.profile_picture ?? null);
      setRelatedProperties(detail.related_properties ?? []);
      setRelatedCompanies(detail.related_companies ?? []);
      setRelatedContacts(detail.related_contacts ?? []);
      setLoading(false);
    };

    loadContactDetail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, resolvedContactId]);

  useEffect(() => {
    if (resolvedContactId && searchParams.get("id") !== String(resolvedContactId)) {
      navigate(`${routeNames.CONTACT_DETAILS}?id=${resolvedContactId}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [resolvedContactId, navigate, searchParams, location.state]);

  const handleImageChange = (e) => {
    if (e.target.files[0]) changeImages(e.target.files[0]);
  };

  const deleteImage = () => {
    if (!canUpdateContactImage) return;
    changeImages();
  };

  const changeImages = (image) => {
    if (!canUpdateContactImage) return;
    const url = backEndUrl + appConfig.backend.routes.contacts.updatecontactImages;

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    axios
      .post(
        url,
        {
          contact_id: resolvedContactId,
          profile_picture: image ? image : {},
        },
        config,
      )
      .then(() => {
        setItemImage(image ? URL.createObjectURL(image) : null);
      });
  };

  if (!resolvedContactId) {
    return null;
  }

  if (!contactData) {
    return (
      <div className="main-container-contactDetails">
        <div className="section-main-container-contactDetails">
          <p className="contact-details-loading">Loading contact…</p>
        </div>
      </div>
    );
  }

  const data = contactData;
  const linkedUser = isAdminUser ? data.linked_user : null;
  const linkedUserDisplayName = isAdminUser ? formatLinkedUserDisplay(linkedUser) : "";
  const linkedUserEmail =
    isAdminUser && linkedUser?.email != null && String(linkedUser.email).trim() !== "" ? String(linkedUser.email).trim() : "—";
  const linkedUserUsername =
    isAdminUser && linkedUser?.username != null && String(linkedUser.username).trim() !== "" ? String(linkedUser.username).trim() : "—";
  const linkedUserRoleLabel = isAdminUser && linkedUser ? formatLinkedUserRole(linkedUser.role) || "—" : "—";

  return (
    <>
      <div className="main-container-contactDetails">
        <div className="section-main-container-contactDetails">
          <div className="section-row-container-contactDetails">
            <div className="section-container-contactDetails">
              <div className="section-container-contactDetails-contactInfo">
                <div className="column-contactInfo-profile-picture">
                  <div>
                    <img src={itemImage ? itemImage : myImage} alt="Contact" />
                  </div>
                  {canUpdateContactImage && (
                    <div className="image-controls">
                      <label className="image-btn">
                        <input type="file" id="propertyImage" accept="image/*,.pdf" onChange={(e) => handleImageChange(e)} />
                        Edit Image
                      </label>
                      {itemImage !== null ? (
                        <Button
                          className="delete-image-btn-items"
                          buttonText={<img style={{ height: "75%" }} src={trash} alt="Delete" />}
                          onClick={() => deleteImage()}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="column-contactInfo">
                  <h2>
                    {data.first_name} {data.last_name} Info
                  </h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Email:</strong> {data.email}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Phone:</strong> {data.phone_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Category:</strong> {data.category?.name ?? ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Company:</strong> {data.company?.name ?? ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Website:</strong> {data.website !== null ? data.website : ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Comment:</strong> {data.comment !== null ? data.comment : ""}
                    </p>
                  </div>
                  {isAdminUser && (
                    <>
                      <div className="label-text-container">
                        <p>
                          <strong>Linked User:</strong> {linkedUserDisplayName || "—"}
                        </p>
                      </div>
                      <div className="label-text-container">
                        <p>
                          <strong>Linked User Email:</strong> {linkedUserEmail}
                        </p>
                      </div>
                      <div className="label-text-container">
                        <p>
                          <strong>Linked User Username:</strong> {linkedUserUsername}
                        </p>
                      </div>
                      <div className="label-text-container">
                        <p>
                          <strong>Linked User Role:</strong> {linkedUserRoleLabel}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="column-contactInfo">
                  <h2>
                    {data.first_name} {data.last_name} Address
                  </h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Number:</strong> {data.street_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Name:</strong> {data.street_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>City:</strong> {data.city}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>State:</strong> {data.state}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Country:</strong> {data.country}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Zip Code:</strong> {data.zip_code}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Bank address & bank info — hidden for now
          <div className="section-row-container-contactDetails">
            <div className="section-container-contactDetails">
              <div className="section-container-contactDetails-bankInfo">
                <div className="column-contactInfo">
                  <h2>
                    {data.first_name} {data.last_name} Bank Address
                  </h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Number:</strong> {data.bank_street_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Name:</strong> {data.bank_street_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>City:</strong> {data.bank_city}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>State:</strong> {data.bank_state}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Country:</strong> {data.bank_country}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Zip Code:</strong> {data.bank_zip_code}
                    </p>
                  </div>
                </div>
                <div className="column-contactInfo">
                  <h2>
                    {data.first_name} {data.last_name} Bank Info
                  </h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Bank Name:</strong> {data.contact_bank !== null ? data.contact_bank.name : ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Name:</strong> {data.contact_bank_account_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Type:</strong> {data.contact_bank_account_type}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Number:</strong> {data.contact_bank_account_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Routing Number:</strong> {data.contact_bank_routing_number}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          */}
          <div className="section-row-container-contactDetails">
            <div className="section-container-contactDetails">
              <div className="section-container-contactDetails-links">
                <EntityLinksSection
                  relatedProperties={relatedProperties}
                  relatedCompanies={relatedCompanies}
                  relatedContacts={relatedContacts}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ContactDetails;
